/**
 * Landing pages for the most searched conversions (/tool/unit-converter/cm-to-inches): the same converter with the
 * pair preselected, plus the formula, a table of common values and links to the other pairs. The list lives in
 * unitPairs.json so the sitemap script can read the same slugs.
 */
import pairs from "./unitPairs.json";
import { CATEGORIES, convertValue, formatNumber, type Category } from "./units";

export interface UnitPair { slug: string; category: string; from: string; to: string; label: string; abbr: string }

export const UNIT_PAIRS: UnitPair[] = (pairs as string[][]).map(([slug, category, from, to, label, abbr]) => ({ slug, category, from, to, label, abbr }));

export function pairBySlug(slug: string | undefined): UnitPair | undefined {
  return slug ? UNIT_PAIRS.find((p) => p.slug === slug) : undefined;
}

const cat = (p: UnitPair): Category => CATEGORIES.find((c) => c.id === p.category)!;
export const pairConvert = (p: UnitPair, v: number) => convertValue(cat(p), p.from, p.to, v);

export const pairNames = (p: UnitPair) => { const [from, to] = p.label.split(" to "); return { from, to }; };

export function pairH1(p: UnitPair): string { return `${p.label} Converter`; }
export function pairTitle(p: UnitPair): string {
  const full = `${p.label} Converter (${p.abbr})`;
  return full.length <= 60 ? full : `${p.label} Converter`;
}
export function pairMeta(p: UnitPair): string {
  const full = `${p.label}: 1 ${p.from} = ${formatNumber(pairConvert(p, 1))} ${p.to}. Enter any value for an instant answer, with the formula and a table of common values. Runs in your browser.`;
  if (full.length <= 160) return full;
  return `${p.label}: enter any value for an instant answer, with the formula and a table of common values. Runs in your browser.`;
}

/** Values shown in the table: everyday sizes for the unit, or the benchmark temperatures. */
export function commonValues(p: UnitPair): number[] {
  if (p.category === "temperature") return p.from === "celsius" ? [-40, -10, 0, 20, 37, 100, 180, 200] : [-40, 0, 32, 68, 98.6, 212, 350, 400];
  if (p.from === "US cup") return [0.25, 0.33, 0.5, 0.75, 1, 1.5, 2, 4];
  if (p.from === "acre") return [0.25, 0.5, 1, 2, 5, 10, 50, 100];
  return [1, 2, 5, 10, 20, 25, 50, 100, 500, 1000];
}

export function pairTable(p: UnitPair): Array<{ from: string; to: string }> {
  return commonValues(p).map((v) => ({ from: formatNumber(v), to: formatNumber(pairConvert(p, v)) }));
}

/** The rule in words, worked out from the converter itself so the page and the tool cannot disagree. */
export function pairFormula(p: UnitPair): string {
  if (p.category === "temperature") {
    if (p.from === "celsius") return "°F = °C × 9/5 + 32. Multiply the Celsius value by 1.8 and add 32.";
    return "°C = (°F − 32) × 5/9. Subtract 32 from the Fahrenheit value, then multiply by 5/9 (about 0.5556).";
  }
  const f = pairConvert(p, 1);
  const inv = 1 / f;
  return `Multiply the ${p.from} value by ${formatNumber(f)} to get ${p.to}, or divide by ${formatNumber(Number(inv.toPrecision(10)))}. So 1 ${p.from} = ${formatNumber(f)} ${p.to}.`;
}
